import AttachFileIcon from "@mui/icons-material/AttachFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
	getMessageDocuments,
	type MessageDocument,
	uploadMessageDocument,
} from "../../api/documents/message-documents";

export interface MessageDocumentsFieldProps {
	/** The documents currently attached to the message/template being edited. */
	value: MessageDocument[];
	onChange: (documents: MessageDocument[]) => void;
	/** Scopes the library picker: this assignment's and its company's documents
	 *  are listed first. Null/undefined outside an assignment context. */
	assignmentId?: number | null;
	disabled?: boolean;
}

const formatDocumentSize = (bytes: number): string =>
	bytes >= 1024 * 1024
		? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
		: `${Math.max(1, Math.round(bytes / 1024))} KB`;

interface LibraryGroup {
	label: string;
	documents: MessageDocument[];
}

const groupLibrary = (
	documents: MessageDocument[],
	assignmentId: number | null | undefined
): LibraryGroup[] => {
	const groups: LibraryGroup[] = [];
	const assignmentDocs = documents.filter(
		(doc) => doc.assignment !== null && doc.assignment === assignmentId
	);
	const companyDocs = documents.filter(
		(doc) => doc.company !== null && doc.assignment === null
	);
	const otherDocs = documents.filter(
		(doc) => !assignmentDocs.includes(doc) && !companyDocs.includes(doc)
	);
	if (assignmentDocs.length > 0)
		groups.push({ label: "This assignment", documents: assignmentDocs });
	if (companyDocs.length > 0)
		groups.push({ label: "Company", documents: companyDocs });
	if (otherDocs.length > 0)
		groups.push({ label: "Other documents", documents: otherDocs });
	return groups;
};

/** Attachment control shared by every message-composition surface: shows the
 *  attached documents as removable chips and opens a picker over the document
 *  library, with upload-from-disk. The caller owns the selection. */
export const MessageDocumentsField = ({
	value,
	onChange,
	assignmentId,
	disabled = false,
}: MessageDocumentsFieldProps) => {
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [library, setLibrary] = useState<MessageDocument[]>([]);
	const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [search, setSearch] = useState("");
	const fileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!isPickerOpen) return;
		setIsLoadingLibrary(true);
		getMessageDocuments(assignmentId)
			.then((res) => setLibrary(res.results))
			.finally(() => setIsLoadingLibrary(false));
	}, [isPickerOpen, assignmentId]);

	const isSelected = (doc: MessageDocument): boolean =>
		value.some((selected) => selected.id === doc.id);

	const toggleDocument = (doc: MessageDocument) => {
		onChange(
			isSelected(doc)
				? value.filter((selected) => selected.id !== doc.id)
				: [...value, doc]
		);
	};

	const handleUpload = async (file: File) => {
		setIsUploading(true);
		try {
			const uploaded = await uploadMessageDocument(file, assignmentId);
			setLibrary((prev) => [uploaded, ...prev]);
			onChange([...value, uploaded]);
		} finally {
			setIsUploading(false);
		}
	};

	const visibleLibrary = library.filter((doc) =>
		doc.name.toLowerCase().includes(search.toLowerCase())
	);

	return (
		<Box data-component="message-documents-field">
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					flexWrap: "wrap",
				}}
			>
				<Button
					data-component="message-documents-attach-button"
					size="small"
					startIcon={<AttachFileIcon />}
					onClick={() => setIsPickerOpen(true)}
					disabled={disabled}
				>
					Attach
				</Button>
				{value.map((doc) => (
					<Chip
						data-component="message-document-chip"
						key={doc.id}
						icon={<AttachFileIcon />}
						label={`${doc.name} (${formatDocumentSize(doc.size)})`}
						size="small"
						onDelete={disabled ? undefined : () => toggleDocument(doc)}
					/>
				))}
				{value.length === 0 && (
					<Typography
						data-component="message-documents-empty"
						variant="caption"
						color="text.secondary"
					>
						No documents attached
					</Typography>
				)}
			</Box>

			<Dialog
				data-component="message-documents-picker"
				open={isPickerOpen}
				onClose={() => setIsPickerOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle data-component="message-documents-picker-title">
					Attach documents
				</DialogTitle>
				<DialogContent data-component="message-documents-picker-content">
					<TextField
						data-component="message-documents-search"
						type="search"
						size="small"
						fullWidth
						placeholder="Search documents…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						sx={{ mt: 1, mb: 1 }}
					/>
					{isLoadingLibrary ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<List dense data-component="message-documents-library">
							{groupLibrary(visibleLibrary, assignmentId).map((group) => [
								<ListSubheader
									key={group.label}
									data-component="message-documents-group"
									disableSticky
								>
									{group.label}
								</ListSubheader>,
								...group.documents.map((doc) => (
									<ListItemButton
										data-component="message-documents-option"
										key={doc.id}
										onClick={() => toggleDocument(doc)}
									>
										<ListItemIcon>
											<Checkbox
												edge="start"
												size="small"
												checked={isSelected(doc)}
												tabIndex={-1}
												disableRipple
											/>
										</ListItemIcon>
										<ListItemText
											primary={doc.name}
											secondary={formatDocumentSize(doc.size)}
										/>
									</ListItemButton>
								)),
							])}
						</List>
					)}
				</DialogContent>
				<DialogActions data-component="message-documents-picker-actions">
					<Button
						data-component="message-documents-upload-button"
						startIcon={<UploadFileIcon />}
						component="label"
						disabled={isUploading}
					>
						{isUploading ? "Uploading…" : "Upload new"}
						<input
							type="file"
							hidden
							ref={fileInputRef}
							accept=".pdf,.docx,.pptx,.png,.jpg"
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (file) void handleUpload(file);
								e.target.value = "";
							}}
						/>
					</Button>
					<Box sx={{ flex: 1 }} />
					<Button
						data-component="message-documents-picker-done"
						variant="contained"
						onClick={() => setIsPickerOpen(false)}
					>
						Done
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
